import type {
  CriterionAverageItem,
  CustomChartColor,
  HeatmapMode,
  QualityBlockScore,
  QualityDetailItem,
  QualityZoneCode,
  StabilityZoneCode,
  TrendDirection,
} from '@/entities/dashboards';
import { type ExpertReportResult, getExpertReportResultText } from '@/entities/expert-report';
import {
  DASHBOARD_BLOCK_MAX_HEIGHT,
  EMPTY_VALUE,
  QualityZoneColor,
  type QualityZoneRanges,
  dataGridHeightByRows,
  qualityZoneByRatio,
  zoneBadgeClass,
  zoneBarClass,
  zoneChartFillClass,
  zoneTextClass,
} from '@/shared/lib';

/**
 * Оформление показателя вне шкалы: значения нет либо оно ни о чём не говорит.
 *
 * Серый намеренно не входит в карту цветов зон: зелёный, жёлтый и красный — это оценка,
 * а «нет данных» и «без изменений» оценки не несут и в шкале не участвуют.
 */
const NEUTRAL_BADGE_CLASS = 'bg-muted text-muted-foreground';

/**
 * Классы кликабельного блока дашборда (алерт «Красных зон», зона качества, инсайт, звонок
 * топ-5): единое выделение наведённого блока — приглушение его собственной заливки.
 *
 * Выделение заливкой, а не контуром: блоки лежат в прокручиваемых областях вплотную к краю,
 * и контур наведённого блока обрезался краем области, оставаясь виден лишь частично.
 */
export const DASHBOARD_CLICKABLE_BLOCK_CLASS = 'cursor-pointer hover:brightness-95';

/**
 * Высота DataGrid-таблиц дашборда (стабильность, тренды, отклонения мини-отчёта, критерии
 * звонка): таблица тянется по своим строкам, но не выше общего потолка блоков дашборда, чтобы
 * соседи по ряду обрывались на одной высоте.
 */
export const dashboardGridHeight = (rowCount: number): number =>
  dataGridHeightByRows(rowCount, DASHBOARD_BLOCK_MAX_HEIGHT);

/**
 * Форматирует число с заданным количеством знаков после запятой.
 * Возвращает прочерк ({@link EMPTY_VALUE}) для null/undefined/NaN.
 */
export const fmt = (v: number | null | undefined, digits = 1): string => {
  if (v === null || v === undefined || isNaN(v)) return EMPTY_VALUE;
  return v.toFixed(digits);
};

/**
 * Результат заключения в записи дашборда: балл — видом дашборда (один знак после запятой),
 * значение — значением, набор — перечислением. Само правило показа общее со списком диалогов
 * ({@link getExpertReportResultText}). `null` — результата нет; чем заменить его на экране,
 * решает место показа.
 */
export const getQualityResultText = (result: ExpertReportResult): string | null =>
  getExpertReportResultText(result, (points) => fmt(points));

/** Итог записи в том виде, в каком его дало правило расчёта заключения */
export const getTotalResult = (row: QualityDetailItem): ExpertReportResult => ({
  score: row.totalRating,
  value: row.totalValue,
  values: row.totalValues,
  unit: row.totalUnit,
});

/** Результат блока записи в том виде, в каком его дало правило расчёта блока */
export const getBlockResult = (block: QualityBlockScore | undefined): ExpertReportResult => ({
  score: block?.rating,
  value: block?.value,
  values: block?.values,
  unit: block?.unit,
});

/**
 * Границы периода тепловой карты как пара дат `YYYY-MM-DD`. Нужны, чтобы сузить
 * запрос списка звонков ячейки до её периода и не полагаться на общую (обрезанную)
 * выборку деталей. Границы считаются в UTC (без влияния таймзоны рантайма), а
 * неделя — по ISO 8601 (понедельник–воскресенье), как её считает бэкенд
 * (`isocalendar`), чтобы список ячейки соответствовал самой ячейке.
 *
 * @param period ключ периода: `YYYY-MM` (месяц) или `YYYY-Wnn` (ISO-неделя)
 * @param mode режим агрегации карты
 * @returns границы периода либо `null`, если ключ не распознан
 */
export const getPeriodDateRange = (period: string, mode: HeatmapMode): { from: string; to: string } | null => {
  const fmtDate = (d: Date): string =>
    `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

  if (mode === 'month') {
    const m = /^(\d{4})-(\d{2})$/.exec(period);
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]); // 1..12
    // День 0 следующего месяца == последний день текущего.
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return { from: `${m[1]}-${m[2]}-01`, to: `${m[1]}-${m[2]}-${String(lastDay).padStart(2, '0')}` };
  }

  const m = /^(\d{4})-W(\d{2})$/.exec(period);
  if (!m) return null;
  const isoYear = Number(m[1]);
  const isoWeek = Number(m[2]);
  // Понедельник ISO-недели 1 — понедельник недели, содержащей 4 января.
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const jan4Dow = jan4.getUTCDay() || 7; // 1=Пн..7=Вс
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - (jan4Dow - 1) + (isoWeek - 1) * 7);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return { from: fmtDate(monday), to: fmtDate(sunday) };
};

/**
 * CSS-класс окраски для общего балла (шкала 0–40).
 * >= 35 — зелёный, >= 25 — жёлтый, иначе красный; цвет — из общей карты цветов зон.
 */
export const totalScoreClass = (v: number | null): string => {
  if (v === null) return NEUTRAL_BADGE_CLASS;
  if (v >= 35) return zoneBadgeClass.green;
  if (v >= 25) return zoneBadgeClass.yellow;
  return zoneBadgeClass.red;
};

/**
 * CSS-класс окраски для балла по отдельному критерию (шкала 0–5).
 * >= 4 — зелёный, >= 2 — жёлтый, иначе красный; цвет — из общей карты цветов зон.
 */
export const scoreClass = (v: number | null): string => {
  if (v === null) return NEUTRAL_BADGE_CLASS;
  if (v >= 4) return zoneBadgeClass.green;
  if (v >= 2) return zoneBadgeClass.yellow;
  return zoneBadgeClass.red;
};

/**
 * CSS-класс окраски ячейки heatmap по средней доле балла её оценок от их собственных максимумов.
 *
 * Долю считает бэкенд по каждой оценке отдельно: максимум задан экспертным заключением оценки, и
 * в одну ячейку могут попасть оценки по разным заключениям — делить средний балл на чужой максимум
 * нельзя. Без доли (пустая ячейка, неизвестная шкала) зоны нет — ячейка остаётся нейтральной.
 */
export const heatmapClass = (share: number | null): string => {
  if (share === null) return NEUTRAL_BADGE_CLASS;
  return zoneBadgeClass[qualityZoneByRatio(share)];
};

/**
 * Зона качества критерия мини-отчёта по доле его балла от максимума своей шкалы.
 *
 * Максимум приходит с бэкенда у каждого критерия: шкалы критериев различаются, поэтому
 * зона считается по доле балла, а не по абсолютному порогу. Без балла или максимума зоны
 * нет — оценки не существует. Пороги зон настраиваются пользователем (настройки отображения);
 * без них берутся пороги по умолчанию.
 */
export const critZoneByMax = (
  score: number | null,
  max: number | null,
  ranges?: QualityZoneRanges,
): QualityZoneColor | null => {
  if (score === null || max === null || max <= 0) return null;
  return qualityZoneByRatio(score / max, ranges);
};

/** CSS-класс окраски ячейки критерия мини-отчёта по его зоне; без зоны — нейтральный. */
export const critCellClass = (
  score: number | null,
  max: number | null,
  ranges?: QualityZoneRanges,
): string => {
  const zone = critZoneByMax(score, max, ranges);
  return zone ? zoneBadgeClass[zone] : NEUTRAL_BADGE_CLASS;
};

/**
 * Доля среднего балла критерия от максимума его шкалы — основа для сравнения критериев с разными
 * шкалами. `null`, если максимум не определён (`maxScore` пуст или ≤ 0): долю посчитать нельзя.
 */
export const criterionScoreRatio = (item: CriterionAverageItem): number | null =>
  item.maxScore !== null && item.maxScore > 0 ? item.value / item.maxScore : null;

/**
 * Слабейший и сильнейший критерии по ДОЛЕ балла от максимума своей шкалы, а не по абсолютному
 * среднему: критерий с короткой шкалой у своего потолка не выглядит слабым лишь из-за малого
 * абсолюта, а критерий с длинной шкалой — сильным лишь из-за большого. Учитываются только критерии
 * с определимым максимумом (`maxScore > 0`) — без него долю не посчитать; пустой вход → оба `null`.
 */
export const criteriaExtremesByRatio = (
  items: CriterionAverageItem[],
): { weakest: CriterionAverageItem | null; strongest: CriterionAverageItem | null } => {
  let weakest: CriterionAverageItem | null = null;
  let strongest: CriterionAverageItem | null = null;
  let minRatio = Number.POSITIVE_INFINITY;
  let maxRatio = Number.NEGATIVE_INFINITY;
  for (const item of items) {
    const ratio = criterionScoreRatio(item);
    if (ratio === null) continue;
    if (ratio < minRatio) {
      minRatio = ratio;
      weakest = item;
    }
    if (ratio > maxRatio) {
      maxRatio = ratio;
      strongest = item;
    }
  }
  return { weakest, strongest };
};

/** Порог дельты, ниже которого отклонение считается незначимым и зоны не имеет. */
const DELTA_THRESHOLD = 0.2;

/**
 * CSS-класс окраски дельты (отклонения): рост — зелёный, падение — красный.
 * Заливки у дельты нет — из карты цветов зон берётся только цвет текста.
 */
export const deltaClass = (v: number | null): string => {
  if (v === null) return 'text-muted-foreground';
  if (v > DELTA_THRESHOLD) return `${zoneTextClass.green} font-bold`;
  if (v < -DELTA_THRESHOLD) return `${zoneTextClass.red} font-bold`;
  return 'text-muted-foreground font-bold';
};

/**
 * Текстовое представление дельты со стрелкой направления.
 * Примеры: «↑ +1.5», «↓ -0.8», «→ 0.1», «—».
 */
export const deltaText = (v: number | null): string => {
  if (v === null) return '—';
  if (v > DELTA_THRESHOLD) return `↑ +${v.toFixed(1)}`;
  if (v < -DELTA_THRESHOLD) return `↓ ${v.toFixed(1)}`;
  return `→ ${v.toFixed(1)}`;
};

/** Зона стабильности консультанта → зона цветовой шкалы (стабилен — зелёный, нестабилен — красный). */
export const stabilityZoneColor: Record<StabilityZoneCode, QualityZoneCode> = {
  stable: 'green',
  moderate: 'yellow',
  unstable: 'red',
};

/** Классы бейджа направления тренда: рост — зелёный, падение — красный, без изменений — серый. */
export const trendBadgeClass: Record<TrendDirection, string> = {
  up: zoneBadgeClass.green,
  flat: NEUTRAL_BADGE_CLASS,
  down: zoneBadgeClass.red,
};

/** Стрелка-индикатор направления недельного тренда. */
export const trendArrow: Record<TrendDirection, string> = {
  up: '↑',
  flat: '→',
  down: '↓',
};

/** Форматирует знаковую величину: положительное значение выводится с ведущим «+». */
export const fmtSigned = (v: number, digits = 1): string => `${v > 0 ? '+' : ''}${v.toFixed(digits)}`;

/**
 * Заливки цветов набора для частей рисунка — вариант `fill:`.
 *
 * Цвета зон берутся у самой шкалы зон ({@link zoneChartFillClass}), а не повторяются литералом:
 * показатель, воссозданный пользователем, попадает в цвет стандартного дашборда один в один.
 */
const CHART_COLOR_FILL_CLASS: Record<CustomChartColor, string> = {
  'slice-1': 'fill-chart-slice-1',
  'slice-2': 'fill-chart-slice-2',
  'slice-3': 'fill-chart-slice-3',
  'slice-4': 'fill-chart-slice-4',
  'slice-5': 'fill-chart-slice-5',
  'slice-6': 'fill-chart-slice-6',
  'slice-7': 'fill-chart-slice-7',
  'slice-8': 'fill-chart-slice-8',
  'slice-9': 'fill-chart-slice-9',
  'slice-10': 'fill-chart-slice-10',
  'zone-green': zoneChartFillClass.green,
  'zone-yellow': zoneChartFillClass.yellow,
  'zone-red': zoneChartFillClass.red,
};

/**
 * Заливки цветов набора для блоков разметки — вариант `bg:` карты {@link CHART_COLOR_FILL_CLASS},
 * править обе.
 *
 * Вариантов два, потому что заливка части рисунка задаётся `fill`, а заливка ячейки таблицы или
 * блока показателя — `background`: одним классом обе не покрыть.
 */
const CHART_COLOR_BG_CLASS: Record<CustomChartColor, string> = {
  'slice-1': 'bg-chart-slice-1',
  'slice-2': 'bg-chart-slice-2',
  'slice-3': 'bg-chart-slice-3',
  'slice-4': 'bg-chart-slice-4',
  'slice-5': 'bg-chart-slice-5',
  'slice-6': 'bg-chart-slice-6',
  'slice-7': 'bg-chart-slice-7',
  'slice-8': 'bg-chart-slice-8',
  'slice-9': 'bg-chart-slice-9',
  'slice-10': 'bg-chart-slice-10',
  'zone-green': zoneBarClass.green,
  'zone-yellow': zoneBarClass.yellow,
  'zone-red': zoneBarClass.red,
};

/** Класс заливки части рисунка по коду цвета набора. */
export const slicePaletteClassByCode = (code: CustomChartColor): string => CHART_COLOR_FILL_CLASS[code];

/** Класс заливки блока разметки по коду цвета набора — вариант {@link slicePaletteClassByCode}. */
export const slicePaletteBgClassByCode = (code: CustomChartColor): string => CHART_COLOR_BG_CLASS[code];

/** Цвета палитры долей в том порядке, в каком ими красятся доли, которым цвет не задан. */
const SLICE_PALETTE_CODES: CustomChartColor[] = [
  'slice-1',
  'slice-2',
  'slice-3',
  'slice-4',
  'slice-5',
  'slice-6',
  'slice-7',
  'slice-8',
  'slice-9',
  'slice-10',
];

/** Код цвета палитры долей по порядковому номеру; номер за пределами палитры идёт по кругу. */
const slicePaletteCode = (index: number): CustomChartColor => SLICE_PALETTE_CODES[index % SLICE_PALETTE_CODES.length];

/**
 * CSS-класс цвета доли круговой диаграммы по её порядковому номеру.
 *
 * Цвета набора различимы между собой, а не разной бледности одного: цветом доля отличается от
 * соседних. Долей больше, чем цветов, — набор идёт по кругу.
 */
export const slicePaletteClass = (index: number): string => slicePaletteClassByCode(slicePaletteCode(index));

/** Класс образца доли в легенде по её номеру — вариант {@link slicePaletteClass} для разметки. */
export const slicePaletteBgClass = (index: number): string => slicePaletteBgClassByCode(slicePaletteCode(index));
